import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import { Check, Download, EyeOff, Globe, MessageSquareText, Moon, Plus, Search, Video } from "@fg/icons";
import {
    SuiteAppBar,
    SuiteMenuItem,
    SuiteSearchField,
    initialsOf,
    presenceStatusDotClass,
    presenceStatusLabel,
    type PresenceStatus
} from "@fg/ui-theme";
import { cn } from "@/lib/utils";
import { connectParityApi } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useInstallPrompt } from "@/api/useInstallPrompt";
import { SUPPORTED_LOCALES, useLocale, useT } from "@/i18n";

interface TopBarProps {
    onSearch?: (query: string) => void;
    /** Fired when the user picks a person from the search suggestions —
     *  the shell opens (or starts) a DM with them. */
    onOpenPerson?: (userId: string) => void;
    onNewMeeting?: () => void;
    onOpenQuietHours?: () => void;
    /** Opens the keyboard-shortcuts overlay (the only help surface today). */
    onOpenHelp?: () => void;
    /** Opens notification / quiet-hours preferences. */
    onOpenSettings?: () => void;
    presenceStatus?: PresenceStatus;
    onChangeStatus?: (s: PresenceStatus) => void;
    /** My status message, shown in the account menu. */
    statusNote?: { message: string; emoji?: string | null } | null;
    /** Opens the "Set status message" dialog. */
    onOpenStatusMessage?: () => void;
    /** "Appear offline" is on: others see me as Offline. */
    appearOffline?: boolean;
    onSetAppearOffline?: (appearOffline: boolean) => void;
}

const STATUS_ITEMS: PresenceStatus[] = ["online", "busy", "dnd", "brb", "away"];

const sectionLabel = "px-2 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground";
const radioRow = "flex w-full items-center gap-2.5 rounded px-2 py-2 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent";

/**
 * Connect's top bar: the shared Fowlguard suite bar (apps launcher, brand,
 * Appearance, help, account menu) with Connect's own pieces plugged in —
 * people-suggesting search, Install app, "+ New meeting", and the Teams-style
 * status / status message / language / quiet hours rows in the account menu.
 */
export function TopBar({
    onSearch, onOpenPerson, onNewMeeting, onOpenQuietHours, presenceStatus = "online", onChangeStatus, onOpenHelp, onOpenSettings,
    statusNote, onOpenStatusMessage, appearOffline = false, onSetAppearOffline
}: TopBarProps) {
    const auth = useAuth();
    const token = useAccessToken();
    const t = useT();
    const { locale, setLocale } = useLocale();
    const { canInstall, install } = useInstallPrompt();
    const [searchValue, setSearchValue] = useState("");
    const [suggestOpen, setSuggestOpen] = useState(false);

    // Debounced needle for suggest-as-you-type — same directory search the
    // Chats picker uses, so the top bar suggests people the same way.
    const [debouncedQ, setDebouncedQ] = useState("");
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedQ(searchValue.trim()), 250);
        return () => clearTimeout(handle);
    }, [searchValue]);

    const suggestions = useQuery({
        queryKey: ["topbar-dir-suggest", debouncedQ, token],
        queryFn: () => connectParityApi.searchDirectory(debouncedQ, token),
        enabled: Boolean(token && onOpenPerson) && debouncedQ.length >= 2
    });

    const submitSearch = (value: string) => {
        const q = value.trim();
        setSuggestOpen(false);
        onSearch?.(q);
    };

    // Keyboard navigation through the suggestions: the people, then the
    // "See all results" row (index === people.length). -1 = nothing
    // highlighted, so Enter runs the full search as before.
    const people = suggestions.data ?? [];
    const dropdownVisible = suggestOpen && debouncedQ.length >= 2 && Boolean(onOpenPerson);
    const [activeIndex, setActiveIndex] = useState(-1);
    useEffect(() => setActiveIndex(-1), [debouncedQ, suggestions.data]);

    const openPerson = (userId: string) => {
        setSuggestOpen(false);
        setSearchValue("");
        setActiveIndex(-1);
        onOpenPerson?.(userId);
    };

    const displayName =
        auth.user?.profile.name
        ?? auth.user?.profile.preferred_username
        ?? auth.user?.profile.email
        ?? "you";
    const email = auth.user?.profile.email ?? "";
    // What others see: "Appear offline" wins over the chosen status.
    const shownStatus: PresenceStatus = appearOffline ? "offline" : presenceStatus;
    const noteLabel = statusNote ? `${statusNote.emoji ? `${statusNote.emoji} ` : ""}${statusNote.message}` : "";

    const search = (
        <form
            className="flex w-full justify-center"
            role="search"
            onSubmit={(e) => {
                e.preventDefault();
                submitSearch(searchValue);
            }}
        >
            <SuiteSearchField
                id="connect-search-input"
                type="text"
                icon={<Search className="h-4 w-4" aria-hidden />}
                value={searchValue}
                onChange={(e) => {
                    setSearchValue(e.target.value);
                    setSuggestOpen(true);
                }}
                onFocus={() => setSuggestOpen(true)}
                onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                        e.preventDefault();
                        if (!dropdownVisible) {
                            setSuggestOpen(true);
                            return;
                        }
                        const count = people.length + 1; // + "See all results"
                        setActiveIndex((i) => e.key === "ArrowDown"
                            ? (i + 1) % count
                            : (i <= 0 ? count - 1 : i - 1));
                        return;
                    }
                    if (e.key === "Enter" && dropdownVisible && activeIndex >= 0) {
                        e.preventDefault();
                        const person = people[activeIndex];
                        if (person) openPerson(person.userId);
                        else submitSearch(searchValue);
                        return;
                    }
                    if (e.key === "Escape") {
                        // First Esc closes the list; a second clears the search.
                        if (dropdownVisible) {
                            setSuggestOpen(false);
                            setActiveIndex(-1);
                            return;
                        }
                        setSearchValue("");
                        submitSearch("");
                        (e.target as HTMLInputElement).blur();
                    }
                }}
                placeholder={t("app.search.placeholder")}
                aria-label={t("app.search.placeholder")}
                role="combobox"
                aria-expanded={dropdownVisible}
                aria-controls="connect-search-suggestions"
                aria-activedescendant={dropdownVisible && activeIndex >= 0 ? `connect-search-opt-${activeIndex}` : undefined}
            >
                {dropdownVisible ? (
                    <div
                        id="connect-search-suggestions"
                        role="listbox"
                        className="absolute left-0 right-0 top-9 z-50 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg"
                    >
                        {people.length > 0 ? (
                            <ul>
                                {people.map((u, i) => (
                                    <li key={u.userId}>
                                        <button
                                            id={`connect-search-opt-${i}`}
                                            type="button"
                                            role="option"
                                            aria-selected={activeIndex === i}
                                            tabIndex={-1}
                                            // mousedown beats the input's onBlur close timer
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                openPerson(u.userId);
                                            }}
                                            onMouseEnter={() => setActiveIndex(i)}
                                            className={cn(
                                                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent",
                                                activeIndex === i && "bg-accent"
                                            )}
                                        >
                                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                                                {initialsOf(u.displayName ?? u.email)}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate font-medium">{u.displayName ?? u.email}</span>
                                                <span className="block truncate text-xs text-muted-foreground">{u.email}</span>
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="px-3 py-2 text-xs text-muted-foreground">
                                {suggestions.isFetching ? "Searching…" : "No people found."}
                            </p>
                        )}
                        <button
                            id={`connect-search-opt-${people.length}`}
                            type="button"
                            role="option"
                            aria-selected={activeIndex === people.length}
                            tabIndex={-1}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                submitSearch(searchValue);
                            }}
                            onMouseEnter={() => setActiveIndex(people.length)}
                            className={cn(
                                "flex w-full items-center gap-2 border-t px-3 py-2 text-left text-xs text-muted-foreground hover:bg-accent",
                                activeIndex === people.length && "bg-accent"
                            )}
                        >
                            <Search className="h-3.5 w-3.5" />
                            See all results for &ldquo;{searchValue.trim()}&rdquo;
                        </button>
                    </div>
                ) : null}
            </SuiteSearchField>
        </form>
    );

    const actions = (
        <>
            {canInstall ? (
                <button
                    type="button"
                    onClick={() => void install()}
                    className="suite-appbar-pill mr-1"
                    title={t("app.install")}
                    aria-label={t("app.install")}
                >
                    <Download className="h-3.5 w-3.5" aria-hidden />
                    <span className="hidden sm:inline">{t("app.install")}</span>
                </button>
            ) : null}
            {onNewMeeting ? (
                <button
                    type="button"
                    onClick={onNewMeeting}
                    className="suite-appbar-pill mr-1"
                    title={t("app.newMeeting")}
                    aria-label={t("app.newMeeting")}
                >
                    <Video className="h-3.5 w-3.5" aria-hidden />
                    <span className="hidden sm:inline">{t("app.newMeeting")}</span>
                    <Plus className="h-3 w-3" aria-hidden />
                </button>
            ) : null}
        </>
    );

    const accountSections = (close: () => void) => {
        const run = (fn: () => void) => () => {
            fn();
            close();
        };
        return (
            <>
                {onOpenStatusMessage ? (
                    <SuiteMenuItem icon={MessageSquareText} onClick={run(onOpenStatusMessage)}>
                        <span className={cn(!noteLabel && "text-muted-foreground")} title={noteLabel || undefined}>
                            {noteLabel || "Set status message"}
                        </span>
                    </SuiteMenuItem>
                ) : null}
                <div className={sectionLabel}>Status</div>
                {STATUS_ITEMS.map((s) => {
                    const current = !appearOffline && presenceStatus === s;
                    return (
                        <button
                            key={s}
                            type="button"
                            role="menuitemradio"
                            aria-checked={current}
                            onClick={run(() => onChangeStatus?.(s))}
                            className={cn(radioRow, current && "font-medium")}
                        >
                            <span aria-hidden className="flex h-4 w-4 shrink-0 items-center justify-center">
                                <span className={cn("h-2.5 w-2.5 rounded-full", presenceStatusDotClass(s))} />
                            </span>
                            <span className="flex-1">{presenceStatusLabel(s)}</span>
                            {current ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
                        </button>
                    );
                })}
                {onSetAppearOffline ? (
                    <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={appearOffline}
                        onClick={run(() => onSetAppearOffline(!appearOffline))}
                        title="Others see you as Offline while you keep working"
                        className={cn(radioRow, appearOffline && "font-medium")}
                    >
                        <EyeOff className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                        <span className="flex-1">Appear offline</span>
                        {appearOffline ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
                    </button>
                ) : null}
                <div className="my-1 h-px bg-border" />
                <div className={sectionLabel}>{t("app.language")}</div>
                {SUPPORTED_LOCALES.map((l) => (
                    <button
                        key={l.id}
                        type="button"
                        role="menuitemradio"
                        aria-checked={locale === l.id}
                        onClick={run(() => setLocale(l.id))}
                        className={cn(radioRow, locale === l.id && "font-medium")}
                    >
                        <Globe className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                        <span className="flex-1">{l.nativeLabel}</span>
                        {locale === l.id ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
                    </button>
                ))}
                {onOpenQuietHours ? (
                    <>
                        <div className="my-1 h-px bg-border" />
                        <SuiteMenuItem icon={Moon} onClick={run(onOpenQuietHours)}>{t("app.quietHours")}</SuiteMenuItem>
                    </>
                ) : null}
            </>
        );
    };

    return (
        <SuiteAppBar
            app="connect"
            user={{ name: displayName, email: email || undefined, presence: shownStatus }}
            onSignOut={() => void auth.signoutRedirect()}
            search={search}
            actions={actions}
            onHelp={onOpenHelp}
            onSettings={onOpenSettings}
            onShortcuts={onOpenHelp}
            accountSections={accountSections}
        />
    );
}
